import type { ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useBodyScrollLock } from './useBodyScrollLock'

/** Props for the Modal component. */
export interface ModalProps {
  children: ReactNode
  /**
   * Called when the backdrop (the dimmed area behind the panel) is clicked. Omit to make the
   * modal non-dismissable by backdrop click — e.g. destructive confirmations that shouldn't be
   * closed by an accidental outside click.
   */
  onBackdropClick?: () => void
  /** `id` of the element that labels this dialog, wired to `aria-labelledby`. */
  labelledBy?: string
  /** Backdrop dimming strength. `"solid"` (default) matches most confirmation/form dialogs; `"light"` matches slide-in side panels. */
  backdrop?: 'solid' | 'light'
  /** ARIA role for the dialog container. Use `"alertdialog"` for single-message error/warning popups. Defaults to `"dialog"`. */
  role?: 'dialog' | 'alertdialog'
}

const BACKDROP_CLASSES: Record<'solid' | 'light', string> = {
  solid: 'bg-black/40 backdrop-blur-[2px]',
  light: 'bg-black/20 backdrop-blur-[1px]',
}

/**
 * Centralized modal/dialog overlay. Renders via a React portal directly under `document.body`
 * so it always escapes any ancestor's stacking context — without this, a modal opened from deep
 * inside the page can end up visually *underneath* an unrelated fixed-position element elsewhere
 * in the app (e.g. a persistent sidebar rail) even though the modal's own `z-index` looks higher
 * in its own file, because that ancestor pins the modal's stacking order to its own.
 *
 * Locks page scroll for as long as it's mounted (see {@link useBodyScrollLock}), so callers no
 * longer need to call that hook themselves.
 */
export function Modal({
  children,
  onBackdropClick,
  labelledBy,
  backdrop = 'solid',
  role = 'dialog',
}: ModalProps) {
  useBodyScrollLock()

  return createPortal(
    <div
      // The backdrop below dims the *entire* viewport, sidebar rail included, so there's no need
      // to keep the panel clear of it. This outer element is only the scroll container — it does
      // NOT itself use `items-center` to vertically center the panel (see the inner wrapper for
      // why).
      className="fixed inset-0 z-[60] overflow-x-hidden overflow-y-auto p-4"
      role={role}
      aria-modal="true"
      aria-labelledby={labelledBy}
    >
      <div
        className={`fixed inset-0 ${BACKDROP_CLASSES[backdrop]}`}
        onClick={onBackdropClick}
        aria-hidden="true"
      />
      {/* `min-h-full` + `items-center` (rather than `items-center` on the scroll container
          itself) centers short panels while keeping tall ones scrollable from their own top:
          centering via `align-items` on a container that's also the `overflow-y-auto` scroller
          clips off whatever portion of an overflowing child sits *above* the vertical center —
          that region isn't reachable by scrolling because `scrollTop` can't go negative. Here the
          centering happens on this inner element instead, whose height grows to fit content taller
          than the viewport (min-height is just a floor), so an oversized panel lays out from y=0
          like normal flow — leaving its top (with the `p-4` gap above) reachable by scrolling the
          outer container from the very start.
          min-w-0 overrides the flex item's default automatic min-width (which is based on the
          content's intrinsic size) — without it, a panel with unwrappable content narrower than
          the viewport could still force this wrapper (and the whole fixed overlay) wider than the
          screen, causing the entire modal to overflow horizontally on narrow phones instead of the
          panel's own internals wrapping/shrinking to fit. */}
      <div className="relative z-10 flex min-h-full w-full min-w-0 items-center justify-center">
        {children}
      </div>
    </div>,
    document.body
  )
}
